import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useMutation, useQueryClient } from 'react-query'
import { Dayjs } from 'dayjs'
import { ApiError, reportError } from 'utils/error'
import { usePollUntilPendingBrandPlanSchedules } from './usePollUntilPendingBrandPlanSchedules'
import { scheduleKeys } from './scheduleKeys'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

export type ContentType =
  | 'instagram_post'
  | 'instagram_reel'
  | 'instagram_story'
  | 'tiktok_post'
  | 'youtube_short'
  | 'facebook_post'
  | 'facebook_story'
  | 'linkedin_post'
  | 'twitter_post'
  | 'twitter_thread'
  | 'newsletter'
  | 'blog_post'
  | 'crosspost_post'
  | 'video_crosspost_post'
  | 'google_my_business_post'

export interface Schedule {
  type: ContentType
  topic: string
  post_at: Dayjs
  initial_media_file_ids?: string[]
  excluded_account_ids?: number[]
  account_ids?: number[]
  is_crosspost?: boolean
  tactics?: string[]
  content_type?: string
  is_assigning_topic?: boolean
  is_blank?: boolean
  caption?: string
  /** IDs of floating ScheduleContentContextSource records to associate with this post after creation */
  context_source_ids?: number[]
  avatar_id?: number
  avatar_angle?: string
  avatar_script?: string
  avatar_captions?: boolean
  avatar_caption_style?: string
  video_script?: string
  video_caption_style?: string
  video_voiceover_voice?: string
}

type Data = {}

const addToBrandPlanSchedule = async ({
  workspaceId,
  brandPlanId,
  brandPlanScheduleId,
  schedule,
  signal,
}: {
  workspaceId: number
  brandPlanId: number
  brandPlanScheduleId: number
  schedule: Schedule[]
  signal?: AbortSignal
}) => {
  const res = await sendToApi(
    `/w/${workspaceId}/brand_plans/${brandPlanId}/schedules/${brandPlanScheduleId}/add_more`,
    {
      method: 'POST',
      body: {
        contents: schedule.map((item) => ({
          ...item,
          post_at: item.post_at.toISOString(),
          ...(item.excluded_account_ids &&
            item.excluded_account_ids.length > 0 && { excluded_account_ids: item.excluded_account_ids }),
          ...(item.account_ids && item.account_ids.length > 0 && { account_ids: item.account_ids }),
        })),
      },
      signal,
    },
  )
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    const errorMessage =
      Array.isArray(res.data?.errors) && typeof res.data.errors[0] === 'string'
        ? res.data.errors.join('. ')
        : 'An error occurred! Our team has been notified and we are looking into it.'
    throw new ApiError(errorMessage, res.data?.error_code)
  }
  return res.data
}

export const useAddToBrandPlanSchedule = () => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const pollUntilPendingSchedules = usePollUntilPendingBrandPlanSchedules({})
  const calendarV2 = useFlipperFlag('calendar_v2')

  return useMutation<Data, Error, { schedule: Schedule[]; brandPlanId: number; brandPlanScheduleId: number }>(
    ({ schedule, brandPlanId, brandPlanScheduleId }) =>
      addToBrandPlanSchedule({ workspaceId, schedule, brandPlanId, brandPlanScheduleId }),
    {
      onSuccess: (_data, { brandPlanScheduleId }) => {
        if (calendarV2) {
          queryClient.invalidateQueries(scheduleKeys.detail(brandPlanScheduleId))
          queryClient.invalidateQueries(scheduleKeys.activeBatch(brandPlanScheduleId))
          queryClient.invalidateQueries(filesKeys.partialCalendar(workspaceId))
        } else {
          queryClient.removeQueries(scheduleKeys.details())
        }
        queryClient.invalidateQueries(scheduleKeys.pending(workspaceId))
        queryClient.invalidateQueries(scheduleKeys.all)
        queryClient.invalidateQueries(calendarKeys.all)
        queryClient.invalidateQueries(filesKeys.all(workspaceId))
        pollUntilPendingSchedules()
      },
      onError: (error) => {
        reportError(error)
      },
    },
  )
}
